import type { App, DirectiveBinding } from 'vue'
import { usePermission } from '@/composables/usePermission'

/**
 * 权限指令实现
 * 用法：
 * - <div v-permission="'module:user:create'">单个权限</div>
 * - <div v-permission="['module:user:create', 'module:user:edit']">多个权限（任一即可）</div>
 */
function checkPermissionVisibility(el: HTMLElement, binding: DirectiveBinding) {
  const { hasPermission, hasAnyPermission } = usePermission()

  let visible = false

  if (Array.isArray(binding.value)) {
    // 如果是数组，检查是否有任一权限
    visible = hasAnyPermission(binding.value)
  } else if (typeof binding.value === 'string') {
    // 如果是字符串，检查单个权限
    visible = hasPermission(binding.value)
  } else {
    // 如果没有提供权限代码，默认显示
    visible = true
  }

  if (!visible) {
    el.style.display = 'none'
    el.setAttribute('data-permission-hidden', 'true')
  } else {
    el.style.display = ''
    el.removeAttribute('data-permission-hidden')
  }
}

/**
 * 权限指令定义
 */
export const permissionDirective = {
  name: 'permission',

  // 元素挂载时
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    checkPermissionVisibility(el, binding)
  },

  // 指令参数更新时
  updated(el: HTMLElement, binding: DirectiveBinding) {
    // 只有当权限代码发生变化时才重新检查
    if (binding.value !== binding.oldValue) {
      checkPermissionVisibility(el, binding)
    }
  }
}

/**
 * 安装权限指令到Vue应用
 * @param app Vue应用实例
 */
export function setupPermissionDirective(app: App) {
  app.directive(permissionDirective.name, permissionDirective)
}

/**
 * 权限指令的默认导出（用于单独注册）
 */
export default permissionDirective